import { Error as ErrorIcon, ExpandLess, ExpandMore, Lock } from '@mui/icons-material';
import { Chip, Grid } from '@mui/material';
import { addSeconds, dateFormatShortTime, parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

// import LiveIcon from '../../../../../assets/svg/Plan/MenuBar/LiveIcon';
import More3Points from '../../../../../assets/svg/More3Points';
import { StopsIcon } from '../../../../../assets/svg/Plan/MenuBar/StopsIcon';
import { numericToText } from '../../../../../components/form-fields/FormTimeWindows';
import { DriversActionsMenu } from '../../../../../components/menus/DriversActionsMenu';
import RouteAvatar from '../../../../../components/RouteAvatar';
import { Driver } from '../../../../../domain/driver/Driver';
import { useRetrieveActivePlanDrivers } from '../../../../../hooks/plan/useRetrieveActivePlanDrivers';
import { useSetActivePlanSelectedDrivers } from '../../../../../hooks/plan/useSetActivePlanSelectedDrivers';
import { useSetActivePlanSelectedStops } from '../../../../../hooks/plan/useSetActivePlanSelectedStops';
import { useRetrieveActiveProject } from '../../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import Text from '../../../../../react-components/Text';
import { driverTimewindowIsInvalid } from '../../../shared/BrokenConstraintsTooltip';
import { DriverRouteProgress } from '../../../shared/DriverRouteProgress';
import ProblemsTooltip from '../../../shared/ProblemsTooltip';
import { DriverStopsList } from './DriverStopsList';

const DriverChipContainer = styled(Grid)`
  margin-bottom: 8px;
  border: 1px solid #e9e9e9;
  border-radius: 4px;
`;

const DriverChipHeader = styled(Grid)`
  height: 68px;
  background-color: white;
  padding: 4px;
  position: relative;
  border-bottom: 1px solid #e9e9e9;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
`;

const DriverChipFooter = styled(Grid)<{ selected: boolean }>`
  height: 32px;
  background-color: ${(props) => (props.selected ? RoutalPalette.neutral05 : `#fbfbfb`)};
  text-align: center;
  cursor: pointer;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
`;

const DriverHeaderRow = styled(Grid)`
  height: 28px;
  padding-left: 8px;
  padding-right: 8px;
`;

export const DriverChip = ({ driver }: { driver: Driver }) => {
  const { setRightPanelInfo } = useRetrieveRightPanelInfo();
  const [expanded, setExpanded] = useState<boolean>(false);
  const [isHover, setIsHover] = useState<boolean>(false);

  const [contextAnchorDriverChip, setContextAnchorDriverChip] = useState<
    | {
        screenPosition: {
          top: number;
          left: number;
        };
      }
    | undefined
  >(undefined);

  const { selectedDrivers, setSelectedDrivers } = useSetActivePlanSelectedDrivers();
  const { data: drivers, map: driversMap } = useRetrieveActivePlanDrivers();
  const { data: activeProject } = useRetrieveActiveProject();
  const { selectedStops } = useSetActivePlanSelectedStops();

  const estimated_last_stop_time =
    driver.stops.length > 0 ? driver.stops[driver.stops.length - 1].estimated_arrival_time : null;

  const getLoadedWeight = () => {
    const weight = driver.stops.reduce((acc, stop) => acc + (stop.weight || 0), 0);
    return Math.floor(weight);
  };
  const getWeightPercent = () => {
    const { max_weight } = driver;
    const weight = driver.stops.reduce((acc, stop) => acc + (stop.weight || 0), 0);
    return Math.floor((weight / (max_weight || 0)) * 100);
  };
  const getLoadedVolume = () => {
    const volume = driver.stops.reduce((acc, stop) => acc + (stop.volume || 0), 0);
    return Math.floor(volume);
  };
  const getVolumePercent = () => {
    const { max_volume } = driver;
    const volume = driver.stops.reduce((acc, stop) => acc + (stop.volume || 0), 0);
    return Math.floor((volume / (max_volume || 0)) * 100);
  };

  const getTimeWindowsPercent = () => {
    const { time_window } = driver;
    if (!time_window) {
      return 0;
    }
    const [start, end] = time_window;
    const estimated = end - start;
    const used = (driver.planned_end_time || 0) - (driver.planned_start_time || 0);
    return Math.floor((used / estimated) * 100);
  };

  const renderHours = () => (
    <Grid item style={{ width: `110px`, marginTop: `2px` }}>
      {driver.is_locked && (
        <Grid
          item
          style={{
            position: `absolute`,
            left: `10px`,
            top: `6px`,
            width: `24px`,
            height: `24px`,
            textAlign: `left`,
          }}
        >
          <Lock
            style={{
              fontSize: `14px`,
              color: RoutalPalette.secondary40,
              backgroundColor: RoutalPalette.white,
              border: `1px solid ${RoutalPalette.white}`,
              borderRadius: `50%`,
            }}
          />
        </Grid>
      )}
      {driver.status === `not_started` &&
      (driver.planned_start_time || driver.planned_start_time === 0) &&
      (driver.planned_end_time || driver.planned_end_time === 0) ? (
        <Text variant="label" style={{ display: `flex` }}>
          {parseSecondsToHM(driver.planned_start_time)}
          <span style={{ margin: `0 2px` }}>-</span>
          {parseSecondsToHM(driver.planned_end_time)}
        </Text>
      ) : null}
      {driver.status === `in_transit` && driver.start_date ? (
        <Text variant="label" style={{ display: `flex` }}>
          <span style={{ color: RoutalPalette.warning }}>{dateFormatShortTime(driver.start_date)}</span>
          {driver.planned_end_time !== undefined && driver.planned_start_time !== undefined ? (
            <>
              <span style={{ margin: `0 2px` }}>-</span>
              <span style={{ color: RoutalPalette.warning }}>
                {dateFormatShortTime(
                  estimated_last_stop_time
                    ? estimated_last_stop_time
                    : addSeconds(new Date(driver.start_date), driver.planned_end_time - driver.planned_start_time)
                )}
              </span>
            </>
          ) : null}
        </Text>
      ) : null}
      {driver.status === `finished` && (driver.start_date || driver.end_date) ? (
        <Text variant="label" style={{ display: `flex` }}>
          {driver.start_date ? (
            <span style={{ color: RoutalPalette.statusColors.completed }}>
              {dateFormatShortTime(driver.start_date)}
            </span>
          ) : null}

          <span style={{ color: RoutalPalette.statusColors.completed, margin: `0 2px` }}>-</span>

          {driver.end_date ? (
            <span style={{ color: RoutalPalette.statusColors.completed }}>{dateFormatShortTime(driver.end_date)}</span>
          ) : null}
        </Text>
      ) : null}
    </Grid>
  );

  const renderConstraints = () => (
    <>
      {activeProject?.view.constraints.time_windows && driver.time_window ? (
        <Chip
          style={{
            marginRight: `2px`,
            height: `18px`,
            ...(driverTimewindowIsInvalid(driver)
              ? { background: RoutalPalette.danger }
              : {
                  background: `linear-gradient(90deg, ${RoutalPalette.success} ${getTimeWindowsPercent()}%, ${
                    RoutalPalette.neutral00
                  } ${getTimeWindowsPercent()}%)`,
                }),
          }}
          label={
            <Text
              variant="label"
              style={{
                color: RoutalPalette.neutral60,
                ...(driverTimewindowIsInvalid(driver) ? { color: `white` } : {}),
              }}
            >
              {numericToText(driver.time_window)}
            </Text>
          }
        />
      ) : null}
      {activeProject?.view.constraints.weight && driver.max_weight ? (
        <Chip
          style={{
            height: `18px`,
            marginRight: `2px`,
            ...(getWeightPercent() > 100
              ? { background: RoutalPalette.danger }
              : {
                  background: `linear-gradient(90deg, ${RoutalPalette.success} ${getWeightPercent()}%, ${
                    RoutalPalette.neutral00
                  } ${getWeightPercent()}%)`,
                }),
          }}
          label={
            <Text
              variant="label"
              style={{
                color: RoutalPalette.neutral60,
                ...(getWeightPercent() > 100 ? { color: RoutalPalette.neutral00 } : {}),
              }}
            >{`${getLoadedWeight()} / ${
              Math.floor(driver.max_weight) !== driver.max_weight ? driver.max_weight.toFixed(2) : driver.max_weight
            }${activeProject?.units?.weight}`}</Text>
          }
        />
      ) : null}
      {/* TODO: VOLUME ERRORS */}
      {activeProject?.view.constraints.volume && driver.max_volume ? (
        <Chip
          style={{
            height: `18px`,
            marginRight: `2px`,
            ...(getVolumePercent() > 100
              ? { background: RoutalPalette.danger }
              : {
                  background: `linear-gradient(90deg, ${RoutalPalette.success} ${getVolumePercent()}%, ${
                    RoutalPalette.neutral00
                  } ${getVolumePercent()}%)`,
                }),
          }}
          label={
            <Text
              variant="label"
              style={{
                color: RoutalPalette.neutral60,
                ...(getVolumePercent() > 100 ? { color: RoutalPalette.neutral00 } : {}),
              }}
            >{`${getLoadedVolume()} / ${
              Math.floor(driver.max_volume) !== driver.max_volume ? driver.max_volume.toFixed(2) : driver.max_volume
            }${activeProject?.units?.volume}`}</Text>
          }
        />
      ) : null}
    </>
  );

  const onDoubleClickDriver = ({ driver }: { driver: Driver }) => {
    setRightPanelInfo({
      detailType: `driver`,
      detailId: `${driver.id}`,
      detailTab: null,
    });
  };

  const isSelected = selectedDrivers?.includes(driver.id);
  const containSelectedStops = selectedStops?.some((stopId) => driver.stops.some((stop) => stop.id === stopId));

  return (
    <DriverChipContainer
      onDoubleClick={() => {
        onDoubleClickDriver({ driver });
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isSelected) {
          setSelectedDrivers([driver.id]);
        }
        setContextAnchorDriverChip({
          screenPosition: {
            top: e.clientY,
            left: e.clientX,
          },
        });
      }}
    >
      <DriverChipHeader
        container
        direction="row"
        wrap="nowrap"
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        style={{ backgroundColor: isSelected ? RoutalPalette.neutral05 : `white` }}
      >
        {/* <DriverStatusBar status={driver.status} /> */}
        <Grid container alignContent="center">
          <DriverHeaderRow container direction="row" alignItems="center" justifyContent="space-between" wrap="nowrap">
            <Grid container direction="row" alignItems="center" wrap="nowrap">
              <Grid
                item
                style={{
                  position: `absolute`,
                  left: `14px`,
                  top: `4px`,
                  width: `24px`,
                  height: `24px`,
                  textAlign: `left`,
                }}
              >
                <ProblemsTooltip type="driver" driver={driver} tooltipPlacement="right">
                  <ErrorIcon
                    style={{
                      color: RoutalPalette.danger,
                      fontSize: `16px`,
                      backgroundColor: RoutalPalette.neutral00,
                      borderRadius: `50%`,
                      marginTop: `-4px`,
                      marginLeft: `-4px`,
                    }}
                  />
                </ProblemsTooltip>
              </Grid>

              <RouteAvatar driver={driver} planDrivers={drivers} />
              <HSpacer small />
              <RoutalTooltip
                mouseEnterDelay={0.5}
                mouseLeaveDelay={0}
                id={driver.id}
                title={driver.label ?? `No name`}
                placement="top"
              >
                {(driver.label ?? ``) !== `` ? (
                  <Text
                    variant="body"
                    style={{
                      fontWeight: 600,
                      cursor: `pointer`,
                      width: `229px`,
                      textOverflow: `ellipsis`,
                      overflow: `hidden`,
                      whiteSpace: `nowrap`,
                    }}
                  >
                    {driver.label}
                  </Text>
                ) : (
                  <Text
                    variant="body"
                    intlId="no_name"
                    style={{ color: RoutalPalette.neutral20, fontWeight: 500, cursor: `pointer` }}
                  />
                )}
              </RoutalTooltip>
            </Grid>
            {/* {driver.status !== `not_started` ? (
              <RoutalTooltip
                mouseEnterDelay={0.5}
                mouseLeaveDelay={0}
                id={`${driver.id}-tracing-percent`}
                title={intl.formatMessage({ id: `plans.plan.live.header.route.tracing_percent` })}
                placement="top"
              >
                <Grid
                  item
                  container
                  direction="row"
                  wrap="nowrap"
                  alignItems="center"
                  style={{ width: `unset`, marginRight: `4px` }}
                >
                  <LiveIcon style={{ color: `#393939`, height: `14px` }} />
                  <Text variant="label" style={{ color: `#393939` }}>
                    {(driver.tracing_percent ? driver.tracing_percent * 100 : 0).toFixed(0)}%
                  </Text>
                </Grid>
              </RoutalTooltip>
            ) : null} */}
            {renderHours()}
          </DriverHeaderRow>
          <DriverHeaderRow container alignItems="center">
            {driver.status === `not_started` && renderConstraints()}
            {driver.status !== `not_started` && <DriverRouteProgress driver={driver} />}
          </DriverHeaderRow>
        </Grid>
        {isHover && (
          <Grid
            container
            style={{
              position: `absolute`,
              width: `50px`,
              top: 0,
              height: `100%`,
              right: 0,
              background: isSelected
                ? `linear-gradient(90deg, ${RoutalPalette.neutral05 + `00`} 0%, ${RoutalPalette.neutral05} 30%, ${
                    RoutalPalette.neutral05
                  } 100%)`
                : `linear-gradient(90deg, ${RoutalPalette.neutral00 + `00`} 0%, ${RoutalPalette.neutral00} 30%, ${
                    RoutalPalette.neutral00
                  } 100%)`,
              borderRadius: `0 2px 2px 0`,
            }}
            justifyContent="center"
            alignContent="center"
          >
            <DriversActionsMenu
              type="popover"
              drivers={[driver]}
              showViewDetails={true}
              popoverProps={{
                hideArrow: true,
                content: <More3Points />,
                style: { color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
              }}
            />
          </Grid>
        )}
      </DriverChipHeader>

      <DriverStopsList driver={driver} expanded={expanded} />

      <DriverChipFooter
        onClick={() => setExpanded(!expanded)}
        container
        selected={containSelectedStops}
        direction="row"
        alignContent="center"
        justifyContent="center"
      >
        <StopsIcon style={{ width: `20px` }} color={driver.color?.value} />
        <HSpacer small />
        <Text style={{ paddingTop: `2px` }}>
          {driver.stops.length} <FormattedMessage id="planoverview.route.table.header.stops" />
        </Text>
        <HSpacer small />
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </DriverChipFooter>
      {contextAnchorDriverChip && (
        <DriversActionsMenu
          type="contextual"
          setContextAnchor={setContextAnchorDriverChip}
          contextAnchor={{
            screenPosition: contextAnchorDriverChip.screenPosition,
          }}
          drivers={selectedDrivers.map((id) => driversMap[id])}
        />
      )}
    </DriverChipContainer>
  );
};
